import { useEffect, useRef } from "react";
import { Room, Player, Round, Submission } from "@/hooks/useRoom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { getRandomPrompt, PromptCategory } from "@/lib/prompts";
import confetti from "canvas-confetti";
import { Trophy } from "lucide-react";
import RoomHeader from "@/components/RoomHeader";

interface Props {
  room: Room;
  players: Player[];
  round: Round;
  submissions: Submission[];
  me: Player;
  amHost: boolean;
}

const TEAM = {
  purple: { name: "Purple", emoji: "🟣", bg: "bg-team-purple", text: "text-team-purple", border: "border-team-purple" },
  green: { name: "Green", emoji: "🟢", bg: "bg-team-green", text: "text-team-green", border: "border-team-green" },
};

export default function GameReveal({ room, players, round, submissions, me, amHost }: Props) {
  const results = round.results ?? { teams: {}, overallAlign: 0 };
  const overallAlign: number = results.overallAlign ?? 0;
  const fired = useRef(false);

  useEffect(() => {
    if (!fired.current && overallAlign >= 80) {
      fired.current = true;
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } });
    }
  }, [overallAlign]);

  const totalRounds = room.settings.rounds ?? 5;
  const isLast = round.round_number >= totalRounds;

  const nextRound = async () => {
    const category = (room.settings.category as PromptCategory) ?? "random";
    const prompt = getRandomPrompt(category);
    const timer = room.settings.timer ?? 30;
    const ends = new Date(Date.now() + timer * 1000).toISOString();
    const { data: rd } = await supabase
      .from("rounds")
      .insert({
        room_id: room.id,
        round_number: round.round_number + 1,
        prompt,
        status: "collecting",
        ends_at: ends,
      })
      .select()
      .single();
    await supabase
      .from("rooms")
      .update({ status: "playing", current_round_id: rd!.id })
      .eq("id", room.id);
  };

  const endGame = async () => {
    await supabase.from("rooms").update({ status: "finished" }).eq("id", room.id);
  };

  const alignColor =
    overallAlign >= 75 ? "text-success" : overallAlign >= 40 ? "text-warning" : "text-destructive";

  return (
    <main className="min-h-screen px-4 pb-6">
      <RoomHeader room={room} me={me} amHost={amHost} players={players} />
      <div className="max-w-2xl mx-auto space-y-5 animate-pop-in pt-2">
        <div className="text-center text-xs font-semibold text-muted-foreground">
          ROUND {round.round_number} RESULTS
        </div>
        <Card className="p-5 text-center border-2 shadow-card">
          <div className="text-sm text-muted-foreground">{round.prompt}</div>
        </Card>

        <Card className="p-6 text-center border-2 shadow-pop">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Group Alignment
          </div>
          <div className={`text-7xl font-extrabold ${alignColor} my-2 tabular-nums`}>
            {overallAlign}%
          </div>
          <div className="h-3 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full gradient-fun transition-all duration-1000"
              style={{ width: `${overallAlign}%` }}
            />
          </div>
        </Card>

        {(["purple", "green"] as const).map((team) => {
          const t = TEAM[team];
          const teamData = results.teams?.[team];
          if (!teamData) return null;
          const teamPlayers = players.filter((p) => p.team === team);
          return (
            <Card key={team} className={`p-5 border-2 shadow-card`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`h-8 w-8 rounded-full ${t.bg} flex items-center justify-center text-white font-bold`}>
                    {t.emoji}
                  </span>
                  <div>
                    <div className="font-bold">Team {t.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {teamPlayers.length} players · {teamData.alignment}% aligned
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Earned</div>
                  <div className="text-xl font-extrabold text-primary">+{teamData.earned}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {teamData.clusters.map((c: { key: string; values: string[] }, i: number) => {
                  const matched = c.values.length >= 2;
                  return (
                    <div
                      key={i}
                      className={`rounded-2xl px-3 py-2 text-sm font-semibold animate-pop-in ${
                        matched
                          ? `${t.bg} text-white shadow-soft`
                          : "bg-muted text-muted-foreground"
                      }`}
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <div className="flex items-center gap-1.5">
                        {matched && <Trophy className="h-3.5 w-3.5" />}
                        <span>{c.values.join(" · ")}</span>
                        {matched && <span className="opacity-75">×{c.values.length}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}

        <Card className="p-4 border-2 shadow-card">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
            Scoreboard
          </div>
          <div className="space-y-1.5">
            {[...players]
              .sort((a, b) => b.score - a.score)
              .map((p, i) => (
                <div key={p.id} className="flex items-center gap-2 text-sm">
                  <span className="w-5 text-muted-foreground">{i + 1}.</span>
                  <span className={`h-2 w-2 rounded-full ${p.team === "purple" ? "bg-team-purple" : "bg-team-green"}`} />
                  <span className="flex-1 font-semibold">{p.name}</span>
                  <span className="font-extrabold tabular-nums">{p.score}</span>
                </div>
              ))}
          </div>
        </Card>

        {amHost && (
          <Button
            onClick={isLast ? endGame : nextRound}
            size="lg"
            className="w-full h-14 text-base font-bold gradient-primary shadow-pop"
          >
            {isLast ? "End Game" : "Next Round →"}
          </Button>
        )}
        {!amHost && (
          <div className="text-center text-muted-foreground animate-pulse">
            Waiting for host…
          </div>
        )}
      </div>
    </main>
  );
}
